import '@src/modules/shell-ui/infrastructure/styles/main.css';
import { getSqlJsWasmBinary } from 'sqljs-wasm-browser-binary';
import '@src/modules/shell-ui/infrastructure/drivers/ui/components/FormComponent.ts';
import './shellRegisterPageComponents.ts';
import { AppConfig } from '@core/domain/AppConfig.ts';
import { AppContainer } from './AppContainer.ts';
import { ApplicationBootstrap } from './shellBootstrap.ts';
import { createWebComponentsPresentationShell } from '@src/modules/shell-routing/infrastructure/drivers/routes/shellDomMount.ts';
import { buildShellRouteBindings } from './shellRouteBindings.ts';
import { createOfflineSqliteInfrastructure } from '@src/modules/platform/local-sqlite/infrastructure/offlineSqliteBootstrap.ts';
import { migrateLegacyMediaDb } from '@src/modules/platform/local-media/infrastructure/driven/localMediaLegacyMigration.ts';
import { ensureLocalMediaStorageReady, openSharedLocalIdb } from '@core/infrastructure/driven/indexedDb/sharedLocalIdb.ts';
import { showFatalAppError } from '@src/modules/shell-ui/infrastructure/showFatalAppError.ts';

/** Fail fast in prod/PWA when an old cached bundle without inlined WASM is still running. */
if (import.meta.env.SQLJS_WASM_INLINE === '1') {
  const wasmProbe = getSqlJsWasmBinary();
  if (wasmProbe == null || wasmProbe.byteLength === 0) {
    throw new Error(
      'Este build no incluye WASM de SQLite embebido (caché antigua). Recompile, borre datos del sitio y recargue con red.',
    );
  }
}

document.title = AppConfig.getAppDisplayTitle();

function showOfflineBootstrapFailure(err: unknown): void {
  showFatalAppError({
    title: 'Modo offline: no se pudo conectar la base de datos local.',
    hint: 'Compruebe la red, que el servidor sirva /database/local.db y /sqljs/sql-wasm.wasm, y pulse Reintentar.',
    error: err,
  });
}

function showMediaBootstrapFailure(err: unknown): void {
  showFatalAppError({
    title: 'No se pudo preparar el almacenamiento de firmas y fotos.',
    hint: 'Cierre otras pestañas de esta app, evite modo incógnito y pulse Reintentar. Si persiste, actualice la app desde Más → Actualizar aplicación.',
    error: err,
  });
}

async function warmLocalMediaStorage(): Promise<void> {
  await migrateLegacyMediaDb(await openSharedLocalIdb());
  await ensureLocalMediaStorageReady();
}

const offlineInfra = createOfflineSqliteInfrastructure();
let offlineSqliteBootstrapOk = true;
try {
  await offlineInfra.bootstrap();
} catch (e) {
  if (AppConfig.useLocalSqliteClient()) {
    offlineSqliteBootstrapOk = false;
    showOfflineBootstrapFailure(e);
  } else {
    throw e;
  }
}

if (offlineSqliteBootstrapOk && typeof indexedDB !== 'undefined') {
  try {
    await warmLocalMediaStorage();
  } catch (e) {
    if (AppConfig.useLocalSqliteClient()) {
      offlineSqliteBootstrapOk = false;
      showMediaBootstrapFailure(e);
    } else {
      throw e;
    }
  }
}

if (offlineSqliteBootstrapOk) {
  try {
    const presentation = createWebComponentsPresentationShell();
    new ApplicationBootstrap(
      presentation,
      new AppContainer({
        localSqlite: AppConfig.useLocalSqliteClient() ? offlineInfra.getRuntime() : undefined,
      }),
      buildShellRouteBindings,
    ).run();
  } catch (err) {
    showFatalAppError({
      title: 'No se pudo iniciar la aplicación.',
      hint: 'Falló la carga del shell Lit o el arranque de rutas. Recargue o revise la consola.',
      error: err,
    });
  }
}
